import { useCallback, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowClockwiseIcon, PackageIcon } from "@phosphor-icons/react";
import {
  DELIVERY_PAGE_SIZE,
  loadDeliveryPage,
  retryDelivery,
  siteName,
  type DeliveryView,
  type DeliveryWithContext,
  type Paged,
} from "../lib/data.ts";
import { useAsync, useDebounced } from "../lib/useAsync.ts";
import { listTime } from "../lib/format.ts";
import {
  Badge,
  Button,
  Chevron,
  EmptyState,
  ErrorState,
  Pager,
  Panel,
  RowSkeleton,
  Segmented,
  SkeletonReveal,
} from "../components/ui.tsx";
import { LeadDetail } from "../components/LeadDetail.tsx";
import { useLiveRefresh } from "../lib/live.ts";
import type { DeliveryRecord } from "@pipeline/types.ts";

function statusLabel(status: DeliveryRecord["status"]) {
  switch (status) {
    case "dead":
      return { tone: "bad" as const, label: "gave up", why: "Retries exhausted." };
    case "bounced":
      return {
        tone: "bad" as const,
        label: "bounced",
        why: "Their mail server rejected it permanently.",
      };
    case "complained":
      return {
        tone: "warn" as const,
        label: "marked spam",
        why: "The recipient reported it as spam.",
      };
    case "delivered":
      return { tone: "ok" as const, label: "delivered", why: "" };
    case "sent":
      return { tone: "ok" as const, label: "sent", why: "" };
    case "pending":
    case "sending":
      return { tone: "neutral" as const, label: status, why: "" };
    default:
      return { tone: "neutral" as const, label: status, why: "" };
  }
}

/**
 * Problems stays first and stays the default.
 *
 * This screen is the action queue. Showing every successful send by default
 * would bury the four rows that need a human among nine hundred that do not.
 * The other three views exist because the log is the evidence when a partner
 * says "we never received that lead" — but they are somewhere you go, not
 * somewhere you land.
 */
const VIEWS: ReadonlyArray<{ value: DeliveryView; label: string }> = [
  { value: "problems", label: "Problems" },
  { value: "all", label: "All" },
  { value: "sent", label: "Sent" },
  { value: "pending", label: "Pending" },
];

const COPY: Record<DeliveryView, { title: string; sub: string }> = {
  problems: {
    title: "Delivery problems",
    sub: "Leads that did not reach the partner. The lead itself is safe; only the email failed.",
  },
  all: {
    title: "Delivery log",
    sub: "Every send this pipeline has ever attempted, newest first. This is the record if a partner ever asks.",
  },
  sent: {
    title: "Delivered",
    sub: "Sends the mail provider accepted, and the ones their server confirmed.",
  },
  pending: {
    title: "In the queue",
    sub: "Queued or in flight right now. These normally clear within seconds.",
  },
};

const EMPTY: Record<DeliveryView, { title: string; body: string }> = {
  problems: {
    title: "Every delivery landed",
    body: "Nothing has bounced or given up. Failures show here with a one-click retry once the cause is fixed.",
  },
  all: {
    title: "Nothing has been sent yet",
    body: "Once a lead is forwarded, every attempt is recorded here permanently.",
  },
  sent: {
    title: "No completed sends yet",
    body: "Deliveries the provider accepted will be listed here.",
  },
  pending: {
    title: "The queue is empty",
    body: "Nothing is waiting to go out. That is what a healthy pipeline looks like between leads.",
  },
};

export function Deliveries({ notify }: { notify: (tone: "ok" | "bad", text: string) => void }) {
  const [view, setView] = useState<DeliveryView>("problems");
  const [page, setPage] = useState(1);
  // "We never received that lead" gets argued on the lead's content, so a
  // delivery row opens into the same detail panel as the Leads screen.
  const [openId, setOpenId] = useState<string | null>(null);

  const state = useAsync<Paged<DeliveryWithContext>>(
    () => loadDeliveryPage(view, page),
    [view, page],
  );
  const refresh = useDebounced(state.reload, 800);
  useLiveRefresh(refresh);

  const retry = useCallback(
    async (row: DeliveryWithContext) => {
      // Optimistic. In the action queue the row is done with, so it leaves; in
      // a log view it is still a real row, so it just changes state.
      state.patch((current) => ({
        ...current,
        items:
          view === "problems"
            ? current.items.filter((item) => item.delivery.id !== row.delivery.id)
            : current.items.map((item) =>
                item.delivery.id === row.delivery.id
                  ? {
                      ...item,
                      delivery: { ...item.delivery, status: "pending", attempts: 0, lastError: "" },
                    }
                  : item,
              ),
      }));

      try {
        await retryDelivery(row.delivery.id);
        notify("ok", "Queued for another attempt.");
      } catch {
        state.reload();
        notify("bad", "Could not queue that retry. Nothing was changed.");
      }
    },
    [state, notify, view],
  );

  const changeView = useCallback((next: DeliveryView) => {
    setView(next);
    setPage(1);
    setOpenId(null);
  }, []);

  if (state.error && !state.data) {
    return <ErrorState message={state.error} onRetry={state.reload} />;
  }

  const result = state.data;
  const rows = result?.items ?? [];
  const firstLoad = state.loading && !result;
  const busy = state.loading && Boolean(result);
  const copy = COPY[view];

  return (
    <div className="stack stack--5">
      <header className="page-head">
        <div>
          <h1 className="page-head__title">{copy.title}</h1>
          <p className="page-head__sub">{copy.sub}</p>
        </div>
        <Segmented
          label="Which deliveries to show"
          value={view}
          options={VIEWS}
          onChange={changeView}
        />
      </header>

      <Panel flush>
        <SkeletonReveal loading={firstLoad} skeleton={<RowSkeleton rows={3} />}>
          {rows.length === 0 ? (
            <EmptyState
              icon={<PackageIcon size={22} />}
              title={EMPTY[view].title}
              body={EMPTY[view].body}
            />
          ) : (
            <div className="records" data-busy={busy}>
              <AnimatePresence initial={false}>
                {rows.map(({ delivery, lead, destination }) => {
                  const status = statusLabel(delivery.status);
                  const retryable =
                    delivery.status === "dead" ||
                    delivery.status === "bounced" ||
                    delivery.status === "complained";
                  // A delivery whose lead was deleted has no detail to show;
                  // the row stays flat rather than opening onto nothing.
                  const expandable = lead !== null;
                  const open = expandable && openId === delivery.id;
                  return (
                    <motion.article
                      className="lead-row t-acc"
                      data-open={open}
                      key={delivery.id}
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                      style={{ overflow: "hidden" }}
                      transition={{ type: "spring", duration: 0.3, bounce: 0 }}
                    >
                      <div
                        className="record record--delivery"
                        data-expandable={expandable || undefined}
                        onClick={(event) => {
                          if (!expandable) return;
                          if ((event.target as HTMLElement).closest("a, button")) return;
                          setOpenId(open ? null : delivery.id);
                        }}
                      >
                        <div className="record__primary">
                          <p className="record__name">{lead?.name || "Lead no longer available"}</p>
                          <p className="record__meta">
                            {[destination?.name, lead ? siteName(lead.siteKey) : null]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                          {delivery.lastError && (
                            <div className="tags">
                              <span className="tag">
                                <span className="tag__label">error</span>
                                <span className="tag__value">{delivery.lastError.slice(0, 90)}</span>
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="record__contact">
                          <span>{delivery.recipients.join(", ") || "no recipients"}</span>
                          <span className="dim mono" style={{ fontSize: "var(--t-xs)" }}>
                            {delivery.attempts} {delivery.attempts === 1 ? "attempt" : "attempts"} ·{" "}
                            {listTime(delivery.created)}
                          </span>
                        </div>

                        <div className="record__actions">
                          <Badge tone={status.tone}>{status.label}</Badge>
                          {retryable && (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<ArrowClockwiseIcon size={13} weight="bold" />}
                              onClick={() => void retry({ delivery, lead, destination })}
                              aria-label="Queue this delivery for another attempt"
                            >
                              Retry
                            </Button>
                          )}
                        </div>

                        {expandable ? (
                          <button
                            type="button"
                            className="record__chevron t-acc-chevron"
                            aria-expanded={open}
                            aria-label={`${open ? "Hide" : "Show"} details for ${lead.name || "this lead"}`}
                            onClick={() => setOpenId(open ? null : delivery.id)}
                          >
                            <Chevron size={14} />
                          </button>
                        ) : (
                          <span className="record__chevron" aria-hidden="true" />
                        )}
                      </div>

                      {expandable && (
                        <div className="t-acc-panel">
                          <div className="t-acc-panel-inner">
                            <LeadDetail entry={{ lead, deliveries: [{ delivery, destination }] }} />
                          </div>
                        </div>
                      )}
                    </motion.article>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </SkeletonReveal>

        {result && result.totalItems > 0 && (
          <Pager
            page={result.page}
            totalPages={result.totalPages}
            totalItems={result.totalItems}
            perPage={result.perPage || DELIVERY_PAGE_SIZE}
            busy={busy}
            onChange={(next) => {
              setPage(next);
              setOpenId(null);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}
      </Panel>

      {view === "problems" && rows.length > 0 && (
        <p className="muted" style={{ fontSize: "var(--t-xs)" }}>
          Fix the cause first, usually a wrong recipient address on the destination. Retrying reuses
          the original idempotency key, so a message the provider already accepted cannot be sent
          twice.
        </p>
      )}
    </div>
  );
}
